import { Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Grid,
  Link,
  Stack,
  TableBody,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  type Theme,
} from '@mui/material'
import {
  BadgeCheckIcon,
  CoinsIcon,
  PercentIcon,
  SendIcon,
  UndoIcon,
  UserPlusIcon,
  UsersIcon,
  WalletIcon,
} from 'lucide-react'

import { Card, Skeleton } from '@repo/ui'
import { TrendChart } from 'components/analytics'
import { AnalyticsBlock } from 'components/display/Analytics'
import { progressTrackColor } from 'components/display/Progress/Progress'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useReferrerState } from 'hooks'
import type { AffiliatesDashboard, InsidersFirstPromoterReport } from 'hooks/api/useInternalReferrals'
import { ROUTES } from 'routes'
import { relativeOrDateTime } from 'utils'

import { buildStages, formatUsdCents, stepRate, weekLabel, type Stage } from '../../affiliatesModel'
import { formatCount, formatPct } from '../../websiteAnalyticsModel'
import { Cell, EmptyRow, HeadCell, Notice, SectionNote, StatTable, ToneChip } from '../shared'

const ICON = { size: 16, strokeWidth: 1 } as const

/** The Refer a brand KPI row: eight tiles, four across from `md`. */
export const ReferABrandTiles = ({ data }: { data: AffiliatesDashboard }) => {
  const f = data.brand.funnel
  const b = data.brand.sent_breakdown
  const tiles = [
    {
      title: 'Referrals sent',
      term: 'affiliates-referrals-sent',
      icon: <SendIcon {...ICON} />,
      stat: formatCount(f.sent),
      unit: `${formatCount(b.invites_delivered)} invitations · ${formatCount(b.link_signups)} link signups`,
    },
    {
      title: 'Signed up',
      term: 'affiliates-funnel-stage',
      icon: <UserPlusIcon {...ICON} />,
      stat: formatCount(f.signed_up),
      unit: `${formatPct(stepRate(f.sent, f.signed_up))} of sent`,
    },
    {
      title: 'Granted access',
      term: 'affiliates-funnel-stage',
      icon: <BadgeCheckIcon {...ICON} />,
      stat: formatCount(f.granted),
      unit: `${formatPct(stepRate(f.signed_up, f.granted))} of signed up`,
    },
    {
      title: 'Paying',
      term: 'affiliates-funnel-stage',
      icon: <WalletIcon {...ICON} />,
      stat: formatCount(f.paying),
      unit: `${formatCount(f.became_client)} became clients`,
    },
    {
      title: 'Sent to paying',
      term: 'affiliates-sent-to-paying',
      icon: <PercentIcon {...ICON} />,
      stat: formatPct(stepRate(f.sent, f.paying)),
      unit: 'of referrals sent in the range',
    },
    {
      title: 'Credits granted',
      term: 'referral-credits-granted',
      icon: <CoinsIcon {...ICON} />,
      stat: formatCount(data.credits.granted),
      unit: `activations · ${formatCount(data.credits.net)} net`,
    },
    {
      title: 'Credits reversed',
      term: 'referral-credits-reversed',
      icon: <UndoIcon {...ICON} />,
      stat: formatCount(data.credits.reversed),
      unit: `activations · ${formatCount(data.credits.forgiven)} forgiven`,
    },
    {
      title: 'Outstanding now',
      term: 'referral-credits-outstanding',
      icon: <UsersIcon {...ICON} />,
      stat: formatCount(data.exposure.outstanding),
      unit: `referral activations unspent · ${formatCount(data.exposure.companies)} ${data.exposure.companies === 1 ? 'company' : 'companies'}`,
    },
  ]
  return (
    <Grid container spacing={4}>
      {tiles.map((t) => (
        <Grid item xs={12} sm={6} lg={3} key={t.title}>
          <AnalyticsBlock title={t.title} term={t.term} icon={t.icon} stat={t.stat} unit={t.unit} />
        </Grid>
      ))}
    </Grid>
  )
}

/** One funnel rung: label, bar scaled to the first stage, count, step rate. */
const StageRow = ({ stage, max, color }: { stage: Stage; max: number; color: string }) => {
  const theme = useTheme()
  const width = max > 0 ? Math.max(stage.count > 0 ? 2 : 0, Math.round((stage.count / max) * 100)) : 0
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(120px, 170px) minmax(0, 1fr) 56px 52px',
        alignItems: 'center',
        columnGap: 3,
      }}
      data-testid={`affiliates-stage-${stage.key}`}
    >
      <Typography variant="body2" sx={{ fontSize: 13 }}>
        {stage.label}
      </Typography>
      {/* The platform's proportion-bar track, as the Website funnel draws it. */}
      <Box sx={{ height: 10, borderRadius: 5, bgcolor: progressTrackColor(theme) }}>
        <Box sx={{ height: '100%', width: `${width}%`, borderRadius: 5, bgcolor: color }} />
      </Box>
      <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 600, textAlign: 'right' }}>
        {formatCount(stage.count)}
      </Typography>
      {stage.rate === null && stage.key === 'sent' ? (
        <span />
      ) : (
        <InfoTooltip term="affiliates-step-rate">
          <Typography variant="body3" color="text.secondary" sx={{ textAlign: 'right', cursor: 'help' }}>
            {formatPct(stage.rate)}
          </Typography>
        </InfoTooltip>
      )}
    </Box>
  )
}

const StageList = ({ stages, color }: { stages: Stage[]; color: string }) => {
  const max = stages[0]?.count ?? 0
  return (
    <Stack spacing={2}>
      {stages.map((s) => (
        <StageRow key={s.key} stage={s} max={max} color={color} />
      ))}
    </Stack>
  )
}

export const ReferralFunnelCard = ({ data }: { data: AffiliatesDashboard }) => {
  const theme = useTheme()
  const stages = buildStages(data.brand.funnel)
  const { invites_undelivered: undelivered } = data.brand.sent_breakdown
  const { ineligible, superseded } = data.brand.not_counted
  return (
    <Card title="Funnel">
      <Stack spacing={4} sx={{ mt: 4 }}>
        <SectionNote>
          Referrals created in the range, at the furthest stage each has reached today. Each
          percentage is the share of the stage above.
        </SectionNote>
        <StageList stages={stages} color={chartColor(theme, 'sent')} />
        <SectionNote>
          Not in the funnel: {formatCount(ineligible)} ineligible, {formatCount(superseded)}{' '}
          superseded
          {undelivered > 0 ? `, ${formatCount(undelivered)} invitations not delivered` : ''}.
        </SectionNote>
      </Stack>
    </Card>
  )
}

/** Series colours: the house brand purple first, then VERA green (as the Website funnel). */
const chartColor = (
  theme: Theme,
  key: 'sent' | 'became_client' | 'first_paid',
): string =>
  key === 'sent'
    ? theme.palette.brand[500]
    : key === 'became_client'
      ? theme.palette.green[600]
      : theme.palette.brand[300]

const SERIES = [
  { dataKey: 'sent', label: 'Referrals sent' },
  { dataKey: 'became_client', label: 'Became a client' },
  { dataKey: 'first_paid', label: 'First payment' },
] as const

export const ReferralTrendCard = ({ data }: { data: AffiliatesDashboard }) => {
  const theme = useTheme()
  const empty = data.weekly.every((w) => w.sent + w.became_client + w.first_paid === 0)
  return (
    <Card title="Per week">
      <Stack spacing={4} sx={{ mt: 4 }}>
        <SectionNote>
          Each event in the week it happened (weeks start Monday, UTC), so a client here may have
          been referred before the range began.
        </SectionNote>
        <Stack direction="row" spacing={4} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          {SERIES.map((s) => (
            <Stack key={s.dataKey} direction="row" spacing={1.5} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: chartColor(theme, s.dataKey) }} />
              <Typography variant="body3" color="text.secondary">
                {s.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
        {empty ? (
          <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body3" color="text.secondary">
              Nothing sent, converted or paid in this range.
            </Typography>
          </Box>
        ) : (
          <TrendChart
            data={data.weekly}
            xKey="week"
            mark="bar"
            height={200}
            ariaLabel="Referrals sent, new clients and first payments per week"
            formatLabel={weekLabel}
            series={SERIES.map((s) => ({ ...s, color: chartColor(theme, s.dataKey) }))}
          />
        )}
      </Stack>
    </Card>
  )
}

/** The id of the All referrals section on Affiliates → Refer & earn (GtmAffiliatesPage). */
export const ALL_REFERRALS_ANCHOR = 'all-referrals'

export const TopReferrersCard = ({ data }: { data: AffiliatesDashboard }) => {
  const referrer = useReferrerState('Affiliates')
  return (
    <Card
      title="Top referring companies"
      action={
        // VRG-684 — the full table moved from People onto this same tab, below; this jumps to it.
        <Link
          component="button"
          type="button"
          variant="body3"
          onClick={() => document.getElementById(ALL_REFERRALS_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          All referrals
        </Link>
      }
    >
      <Stack spacing={3} sx={{ mt: 4 }}>
        <StatTable>
          <TableHead>
            <TableRow>
              <HeadCell>Company</HeadCell>
              <HeadCell align="right">Referrals</HeadCell>
              <HeadCell align="right">Became clients</HeadCell>
              <HeadCell align="right">Paying</HeadCell>
              <HeadCell align="right">
                <InfoTooltip term="referral-credits-granted" text="Net credits these referrals earned (granted minus reversed).">
                  <span style={{ cursor: 'help' }}>Credits net</span>
                </InfoTooltip>
              </HeadCell>
              <HeadCell align="right">
                <InfoTooltip term="referral-credits-outstanding">
                  <span style={{ cursor: 'help' }}>Outstanding now</span>
                </InfoTooltip>
              </HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.top_companies.length === 0 ? (
              <EmptyRow colSpan={6} text="No company sent a referral in this range." />
            ) : (
              data.top_companies.map((c) => (
                <TableRow key={c.company_id ?? 'former'}>
                  <Cell strong>
                    {c.company_id ? (
                      <Link
                        component={RouterLink}
                        to={`${ROUTES.INTERNAL_COMPANY(c.company_id)}?tab=referrals`}
                        state={referrer}
                        underline="hover"
                        color="inherit"
                      >
                        {c.name ?? 'Unnamed company'}
                      </Link>
                    ) : (
                      'Former brand'
                    )}
                  </Cell>
                  <Cell align="right">{formatCount(c.referrals)}</Cell>
                  <Cell align="right">{formatCount(c.became_client)}</Cell>
                  <Cell align="right">{formatCount(c.paying)}</Cell>
                  <Cell align="right">{formatCount(c.credits_net)}</Cell>
                  <Cell align="right">{formatCount(c.outstanding)}</Cell>
                </TableRow>
              ))
            )}
          </TableBody>
        </StatTable>
        <SectionNote>
          The ten companies with the most new clients from referrals created in the range. A
          company name opens its Referrals tab.
        </SectionNote>
      </Stack>
    </Card>
  )
}

const TRACK_LABELS: Record<string, string> = {
  tracked: 'reported',
  duplicate: 'already reported',
  not_configured: 'not reported (FirstPromoter not connected)',
  rejected: 'rejected by FirstPromoter',
  failed: 'failed to send',
}

/** Vera Insiders from vera-2's own referral records: available before FirstPromoter is. */
export const InsidersVeraCard = ({ data }: { data: AffiliatesDashboard }) => {
  const theme = useTheme()
  const i = data.insiders
  const stages: Stage[] = [
    { key: 'sent', label: 'Prospects', count: i.funnel.prospects, rate: null },
    { key: 'signed_up', label: 'Signed up', count: i.funnel.signed_up, rate: stepRate(i.funnel.prospects, i.funnel.signed_up) },
    { key: 'granted', label: 'Granted access', count: i.funnel.granted, rate: stepRate(i.funnel.signed_up, i.funnel.granted) },
    { key: 'company_created', label: 'Company created', count: i.funnel.company_created, rate: stepRate(i.funnel.granted, i.funnel.company_created) },
    { key: 'became_client', label: 'Became a client', count: i.funnel.became_client, rate: stepRate(i.funnel.company_created, i.funnel.became_client) },
    { key: 'paying', label: 'Paying', count: i.funnel.paying, rate: stepRate(i.funnel.became_client, i.funnel.paying) },
  ]
  const tracks = Object.entries(i.track_status)
  return (
    <Card
      title="Insider prospects"
      action={<ToneChip tone="info" label="From vera-2" glossaryId="insider-prospect" />}
    >
      <Stack spacing={4} sx={{ mt: 4 }}>
        <SectionNote>
          Prospects whose waitlist form carried an Insider reference, created in the range, at the
          furthest stage each has reached.
        </SectionNote>
        <StageList stages={stages} color={theme.palette.brand[500]} />
        <SectionNote>
          {formatCount(i.insiders_with_prospects)}{' '}
          {i.insiders_with_prospects === 1 ? 'Insider' : 'Insiders'} brought these prospects.{' '}
          {formatCount(i.brand_touches_lost_to_insiders)} brand{' '}
          {i.brand_touches_lost_to_insiders === 1 ? 'referral was' : 'referrals were'} refused
          because an Insider already held the prospect.
          {tracks.length > 0
            ? ` Signups sent to FirstPromoter: ${tracks
                .map(([k, n]) => `${formatCount(n)} ${TRACK_LABELS[k] ?? k}`)
                .join(', ')}.`
            : ''}
        </SectionNote>
      </Stack>
    </Card>
  )
}

/** FirstPromoter's own figures, or a plain account of why there are none. Never zeros. */
export const InsidersFirstPromoterCard = ({
  report,
  isLoading,
  failed,
}: {
  report: InsidersFirstPromoterReport | undefined
  isLoading: boolean
  failed: boolean
}) => {
  const f = report?.status === 'ok' ? report.figures : null
  const chip =
    report?.status === 'ok' ? (
      <ToneChip tone="positive" label="Connected" />
    ) : report?.status === 'error' || failed ? (
      <ToneChip tone="negative" label="Unavailable" />
    ) : report?.status === 'not_configured' ? (
      <ToneChip tone="neutral" label="Not connected" />
    ) : null
  return (
    <Card title="FirstPromoter" action={chip}>
      <Stack spacing={4} sx={{ mt: 4 }}>
        {isLoading ? (
          <InsidersFirstPromoterSkeletonBody />
        ) : f ? (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
                gap: 5,
              }}
            >
              <AnalyticsBlock
                surface="bare"
                statSize={24}
                title="Active Insiders"
                term="active-insiders"
                stat={`${formatCount(f.active_insiders)}${f.active_insiders_capped ? '+' : ''}`}
                unit={f.pending_insiders !== null ? `now · ${formatCount(f.pending_insiders)} awaiting review` : 'now'}
              />
              <AnalyticsBlock
                surface="bare"
                statSize={24}
                title="Leads"
                term="insider-leads"
                stat={formatCount(f.leads)}
                unit={`${formatCount(f.signups)} signups · ${formatCount(f.customers)} customers`}
              />
              <AnalyticsBlock
                surface="bare"
                statSize={24}
                title="Commission paid"
                term="insider-commission-paid"
                stat={formatUsdCents(f.commission_paid_cents)}
                unit={`in the range · ${formatUsdCents(f.commission_earned_cents)} earned`}
              />
              <AnalyticsBlock
                surface="bare"
                statSize={24}
                title="Commission owed"
                term="insider-commission-owed"
                stat={formatUsdCents(f.commission_owed_cents)}
                unit="due now, tax included"
              />
            </Box>
            <SectionNote>
              From FirstPromoter, {report?.cached ? 'cached' : 'fetched'}{' '}
              {relativeOrDateTime(report?.computed_at ?? '')}. Refreshed at most every 15 minutes.
            </SectionNote>
          </>
        ) : report?.status === 'not_configured' ? (
          <Notice severity="info">
            Not connected. The FirstPromoter account and its API keys are pending, so there are no
            active Insiders, leads or commission figures to show yet. They appear here once
            FIRSTPROMOTER_API_KEY and FIRSTPROMOTER_ACCOUNT_ID are set on the API. Insider
            prospects on the left come from vera-2 and do not wait for it.
          </Notice>
        ) : (
          <Notice severity="error">
            FirstPromoter could not be read
            {report?.message ? ` (${report.message})` : ''}. Its figures are missing, not zero.
            Reload in a minute.
          </Notice>
        )}
      </Stack>
    </Card>
  )
}

const InsidersFirstPromoterSkeletonBody = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
      gap: 5,
    }}
  >
    {[0, 1, 2, 3].map((k) => (
      <Stack key={k} spacing={2}>
        <Skeleton height={14} width="55%" />
        <Skeleton height={24} width="35%" />
        <Skeleton height={12} width="70%" />
      </Stack>
    ))}
  </Box>
)

/** Mirrors the loaded Refer a brand layout: tiles, funnel | trend, top companies. */
export const ReferABrandSkeleton = () => (
  <Stack spacing={6} useFlexGap>
    <Grid container spacing={4}>
      {Array.from({ length: 8 }, (_, i) => (
        <Grid item xs={12} sm={6} lg={3} key={i}>
          <Card sx={{ p: 6 }}>
            <Skeleton height={16} width="60%" />
            <Skeleton height={32} width="40%" />
            <Skeleton height={12} width="75%" />
          </Card>
        </Grid>
      ))}
    </Grid>
    <Grid container spacing={4}>
      <Grid item xs={12} lg={6}>
        <Card title="Funnel">
          <Stack spacing={3} sx={{ mt: 4 }}>
            <Skeleton height={12} width="80%" />
            {Array.from({ length: 7 }, (_, i) => (
              <Skeleton key={i} height={14} />
            ))}
          </Stack>
        </Card>
      </Grid>
      <Grid item xs={12} lg={6}>
        <Card title="Per week">
          <Stack spacing={3} sx={{ mt: 4 }}>
            <Skeleton height={12} width="80%" />
            <Skeleton height={200} />
          </Stack>
        </Card>
      </Grid>
    </Grid>
    <Card title="Top referring companies">
      <Stack spacing={3} sx={{ mt: 4 }}>
        <Skeleton height={14} lines={6} />
      </Stack>
    </Card>
  </Stack>
)

export const InsidersVeraSkeleton = () => (
  <Card title="Insider prospects">
    <Stack spacing={3} sx={{ mt: 4 }}>
      <Skeleton height={12} width="80%" />
      {Array.from({ length: 6 }, (_, i) => (
        <Skeleton key={i} height={14} />
      ))}
    </Stack>
  </Card>
)
